import LinearProgress from '@material-ui/core/LinearProgress';
import Tooltip from '@material-ui/core/Tooltip';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import clsx from 'clsx';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { localeState } from '../../api/appState';
import {
  ShoppingCartShipment,
  shoppingCartnumberOfSubjectsSelector,
  shoppingCartShipmentSelector,
} from '../../api/shoppingCart';
import useTranslateWithFallback, { TranslateWithFallback } from '../../hooks/useTranslateWithFallback';
import { PalletIcon, ParcelIcon } from './shipmentIcons';

export interface CartShipmentInfoProps {
  className?: string;
  /** `full` variant puts weight under shipment type instead of next to it, for narrow places. */
  stacked?: boolean;
  /**
   * `full` is a box with shipment type, weight and detail. `compact` is a pill with icon and weight only,
   * the rest is in its tooltip.
   */
  variant?: 'full' | 'compact';
}

type CartShipmentInfoClassKey =
  | 'parcel'
  | 'pallet'
  | 'unknown'
  | 'full'
  | 'fullIcon'
  | 'content'
  | 'header'
  | 'headerStacked'
  | 'title'
  | 'weight'
  | 'progress'
  | 'progressBar'
  | 'detail'
  | 'compact'
  | 'compactIcon'
  | 'tooltipTitle';

const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CartShipmentInfoClassKey, {}>({
      parcel: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      },
      pallet: {
        backgroundColor: theme.palette.appBar.background,
        color: theme.palette.primary.main,
      },
      unknown: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.primary.main,
      },
      full: {
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1, 1.5),
        borderRadius: theme.shape.borderRadius,
      },
      fullIcon: {
        fontSize: 34,
        flexShrink: 0,
        marginRight: theme.spacing(1.5),
      },
      content: {
        flexGrow: 1,
        minWidth: 0,
      },
      header: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
      },
      headerStacked: {
        flexDirection: 'column',
        alignItems: 'flex-start',
      },
      title: {
        marginRight: theme.spacing(1),
        fontSize: '0.85rem',
        fontWeight: theme.typography.fontWeightBold,
        letterSpacing: 0.5,
        lineHeight: 1.3,
        textTransform: 'uppercase',
      },
      weight: {
        fontSize: '1.1rem',
        fontWeight: theme.typography.fontWeightBold,
        lineHeight: 1.3,
        whiteSpace: 'nowrap',
      },
      progress: {
        height: 4,
        marginTop: theme.spacing(0.5),
        borderRadius: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.3)',
      },
      progressBar: {
        borderRadius: 2,
        backgroundColor: theme.palette.appBar.background,
      },
      detail: {
        ...theme.typography.caption,
        marginTop: theme.spacing(0.5),
        lineHeight: 1.3,
      },
      compact: {
        display: 'inline-flex',
        alignItems: 'center',
        marginTop: 6,
        padding: '4px 10px',
        borderRadius: 16,
        boxShadow: theme.shadows[3],
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: 0.3,
        whiteSpace: 'nowrap',
        cursor: 'default',
      },
      compactIcon: {
        fontSize: 18,
        marginRight: 6,
      },
      tooltipTitle: {
        fontWeight: theme.typography.fontWeightBold,
      },
    }),
  { name: 'CartShipmentInfo' },
);

function useWeightFormatter(): (weight: number) => string {
  const locale = useRecoilValue(localeState);

  return React.useMemo(() => {
    const numberFormat = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
    return (weight: number) => `${numberFormat.format(weight)}\u00a0kg`;
  }, [locale]);
}

function shipmentTitle({ type }: ShoppingCartShipment, t: TranslateWithFallback): string {
  if (type === 'parcel') return t('shoppingCart/shipment/parcel');
  if (type === 'pallet') return t('shoppingCart/shipment/pallet');
  return t('shoppingCart/shipment/weight');
}

function shipmentDetail(
  { hasPalletOnlyGoods, parcelMaxWeight, type }: ShoppingCartShipment,
  t: TranslateWithFallback,
  formatWeight: (weight: number) => string,
): string | undefined {
  if (hasPalletOnlyGoods) return t('shoppingCart/shipment/palletOnlyGoods');
  if (parcelMaxWeight === undefined) return undefined;
  if (type === 'parcel') return t('shoppingCart/shipment/parcelMaxWeight', { weight: formatWeight(parcelMaxWeight) });
  if (type === 'pallet') {
    return t('shoppingCart/shipment/overParcelMaxWeight', { weight: formatWeight(parcelMaxWeight) });
  }
  return undefined;
}

/**
 * Weight of goods in the cart and whether it goes as a parcel or a pallet right now.
 */
const CartShipmentInfo: React.FC<CartShipmentInfoProps> = ({ className, stacked = false, variant = 'full' }) => {
  const classes = useStyles();
  const t = useTranslateWithFallback();
  const formatWeight = useWeightFormatter();
  const subjectsNumber = useRecoilValue(shoppingCartnumberOfSubjectsSelector);
  const shipment = useRecoilValue(shoppingCartShipmentSelector);

  if (subjectsNumber === 0 || shipment.pending) return null;

  const title = shipmentTitle(shipment, t);
  const detail = shipmentDetail(shipment, t, formatWeight);
  const weight = formatWeight(shipment.weight);
  const Icon = shipment.type === 'pallet' ? PalletIcon : shipment.type === 'parcel' ? ParcelIcon : null;
  const typeClassName = clsx({
    [classes.parcel]: shipment.type === 'parcel',
    [classes.pallet]: shipment.type === 'pallet',
    [classes.unknown]: shipment.type === undefined,
  });

  if (variant === 'compact') {
    const summary = `${title}: ${weight}`;

    return (
      <Tooltip
        arrow
        placement='left'
        title={
          <>
            <div className={classes.tooltipTitle}>{summary}</div>
            {detail && <div>{detail}</div>}
          </>
        }
      >
        <span
          className={clsx(classes.compact, typeClassName, className)}
          role='img'
          aria-label={detail ? `${summary}. ${detail}` : summary}
        >
          {Icon && <Icon className={classes.compactIcon} />}
          {weight}
        </span>
      </Tooltip>
    );
  }

  const parcelFill =
    shipment.type === 'parcel' && shipment.parcelMaxWeight
      ? Math.min(100, (shipment.weight / shipment.parcelMaxWeight) * 100)
      : undefined;

  return (
    <div className={clsx(classes.full, typeClassName, className)}>
      {Icon && <Icon className={classes.fullIcon} />}
      <div className={classes.content}>
        <div className={clsx(classes.header, { [classes.headerStacked]: stacked })}>
          <span className={classes.title}>{title}</span>
          <span className={classes.weight}>{weight}</span>
        </div>
        {parcelFill !== undefined && (
          <LinearProgress
            variant='determinate'
            value={parcelFill}
            classes={{ root: classes.progress, bar: classes.progressBar }}
            aria-label={detail}
          />
        )}
        {detail && <div className={classes.detail}>{detail}</div>}
      </div>
    </div>
  );
};

CartShipmentInfo.displayName = 'CartShipmentInfo';

export default CartShipmentInfo;
